// AdminLaunchEmailerCard — one-click launch-day blast to db.launch_notify.
// Preview first (count + sample), then Send with a confirm dialog so an accidental
// click can't fire before Product Hunt goes live.
import { useEffect, useState } from "react";
import { Rocket, Loader2, Send, AlertTriangle, Check, Users, Mail } from "lucide-react";
import { getLaunchEmailerPreview, sendLaunchEmailerBlast } from "@/lib/api";
import { toast } from "sonner";

export const AdminLaunchEmailerCard = ({ role }) => {
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [promoCode, setPromoCode] = useState("PHUNT50");
  const [phUrl, setPhUrl] = useState("https://www.producthunt.com/products/lectura");

  useEffect(() => {
    if (role !== "admin") return;
    getLaunchEmailerPreview().then(setPreview).catch(setErr);
  }, [role]);

  if (role !== "admin") return null;
  if (err) return null;
  if (preview === null) {
    return (
      <div data-testid="admin-launch-emailer-card" className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading launch emailer…
        </div>
      </div>
    );
  }

  const fire = async () => {
    setSending(true);
    try {
      const res = await sendLaunchEmailerBlast({ promo_code: promoCode.trim() || "PHUNT50", ph_url: phUrl.trim() });
      setResult(res);
      toast.success(`Sent to ${res.sent}. Failed: ${res.failed}.`);
      // Refresh preview to reflect launched_at markers
      try { setPreview(await getLaunchEmailerPreview()); } catch { /* non-blocking */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the blast.");
    } finally {
      setSending(false);
      setShowConfirm(false);
    }
  };

  const total = (preview.recipients_pending || 0) + (preview.already_sent || 0);

  return (
    <div data-testid="admin-launch-emailer-card" className="rounded-2xl border border-border bg-card p-6">
      <div className="flex items-start gap-3">
        <span className="grid place-items-center h-10 w-10 rounded-xl bg-primary text-primary-foreground shrink-0">
          <Rocket className="h-5 w-5" />
        </span>
        <div className="flex-1">
          <h3 className="font-serif text-xl font-bold">Launch-day emailer</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            One-click blast to everyone who signed up on{" "}
            <a href="/launch" className="text-accent underline">/launch</a>. Fires the PHUNT50 promo + Product Hunt link.
            Idempotent — anyone already mailed is skipped on re-runs.
          </p>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-border p-4 text-center">
          <Users className="h-4 w-4 mx-auto text-accent mb-1" />
          <p className="font-serif text-3xl font-bold" data-testid="launch-emailer-pending">{preview.recipients_pending || 0}</p>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">pending</p>
        </div>
        <div className="rounded-xl border border-border p-4 text-center">
          <Check className="h-4 w-4 mx-auto text-accent mb-1" />
          <p className="font-serif text-3xl font-bold" data-testid="launch-emailer-sent">{preview.already_sent || 0}</p>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">already sent</p>
        </div>
      </div>

      {total === 0 ? (
        <p className="mt-5 rounded-xl border border-dashed border-border bg-secondary/40 px-4 py-4 text-center text-sm text-muted-foreground">
          No one on the launch notify list yet. Share <span className="font-mono">/launch</span> to collect signups.
        </p>
      ) : (
        <div className="mt-5 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <input
              data-testid="launch-emailer-promo"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              placeholder="Promo code"
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm font-mono"
            />
            <input
              data-testid="launch-emailer-phurl"
              value={phUrl}
              onChange={(e) => setPhUrl(e.target.value)}
              placeholder="Product Hunt URL"
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
          </div>

          <details className="rounded-xl border border-border bg-secondary/40 px-4 py-2 text-xs">
            <summary className="cursor-pointer font-semibold py-1">Preview email copy</summary>
            <pre className="mt-2 text-[11px] whitespace-pre-wrap font-mono text-muted-foreground max-h-40 overflow-y-auto">{preview.sample_text}</pre>
          </details>

          {!showConfirm ? (
            <button
              data-testid="launch-emailer-open-confirm"
              onClick={() => setShowConfirm(true)}
              disabled={preview.recipients_pending === 0}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground px-5 py-3 text-sm font-bold hover:opacity-90 disabled:opacity-50"
            >
              <Send className="h-4 w-4" /> Send launch blast to {preview.recipients_pending} recipients
            </button>
          ) : (
            <div className="rounded-xl border-2 border-destructive/40 bg-destructive/5 p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                <div>
                  <p className="font-bold text-sm">Send to {preview.recipients_pending} real people?</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    This is a live blast. Make sure Product Hunt is actually live and the promo code exists in Stripe.
                  </p>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  data-testid="launch-emailer-confirm"
                  onClick={fire}
                  disabled={sending}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-destructive text-destructive-foreground px-3 py-2 text-xs font-bold hover:opacity-90 disabled:opacity-50"
                >
                  {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                  Yes, send now
                </button>
                <button
                  data-testid="launch-emailer-cancel"
                  onClick={() => setShowConfirm(false)}
                  disabled={sending}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-2 text-xs font-semibold hover:bg-secondary/60 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {result && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-800 px-4 py-3 text-xs" data-testid="launch-emailer-result">
              <Mail className="inline h-3.5 w-3.5 mr-1.5" />
              Sent {result.sent}. {result.failed > 0 && <>Failed {result.failed}.</>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminLaunchEmailerCard;
